'use client'
import { useState } from 'react'
import { copyTextToClipboard } from 'src/_helpers/copyToClipboard'
import Typography from './Typography'
import Button from './Button'

// Essential button variations showing sizes and icon positions
const BUTTON_VARIANTS = [
  // Size variations
  { size: 'l', icon: 'none', label: 'Large Button (L)', disabled: false, active: false },
  { size: 'm', icon: 'none', label: 'Medium Button (M)', disabled: false, active: false },
  { size: 's', icon: 'none', label: 'Small Button (S)', disabled: false, active: false },

  // Icon position variations
  { size: 'm', icon: 'left', label: 'Left Icon', disabled: false, active: false },
  { size: 'm', icon: 'right', label: 'Right Icon', disabled: false, active: false },

  // States
  { size: 'm', icon: 'none', label: 'Active Button', disabled: false, active: true },
  { size: 'm', icon: 'none', label: 'Disabled Button', disabled: true, active: false },
]

// Dark variant examples
const DARK_BUTTON_VARIANTS = [
  // Size variations
  { size: 'l', icon: 'none', label: 'Large Dark (L)', disabled: false, active: false },
  { size: 'm', icon: 'none', label: 'Medium Dark (M)', disabled: false, active: false },
  { size: 's', icon: 'none', label: 'Small Dark (S)', disabled: false, active: false },

  // Icon position variations
  { size: 'm', icon: 'left', label: 'Dark Left Icon', disabled: false, active: false },
  { size: 'm', icon: 'right', label: 'Dark Right Icon', disabled: false, active: false },

  // States
  { size: 'm', icon: 'none', label: 'Active Dark', disabled: false, active: true },
  { size: 'm', icon: 'none', label: 'Disabled Dark', disabled: true, active: false },
]

const LINK_BUTTON_VARIANTS = [
  { size: 'l', icon: 'left', label: 'Link Light', href: '/articles', variant: 'link-light' },
  { size: 'l', icon: 'left', label: 'Link Dark', href: '/collections', variant: 'link-dark' },
]

const ButtonExample = ({ config, cardVariant = 'light' }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (text) => {
    const success = await copyTextToClipboard(text)
    if (!success) return

    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Generate component code based on config
  const generateComponentCode = () => {
    const { size, icon, disabled, active, href, variant } = config
    const disabledAttr = disabled ? ' disabled' : ''
    const iconAttr = icon !== 'none' ? ` icon="${icon}"` : ''
    const sizeAttr = size === 'm' ? '' : ` size="${size}"`
    const buttonVariant = variant || cardVariant
    const variantAttr = buttonVariant === 'light' ? '' : ` variant="${buttonVariant}"`
    const hrefAttr = href ? ` href="${href}"` : ''
    // Note: active prop is just for demonstration - actual active state comes from user interaction

    return `<Button${sizeAttr}${iconAttr}${variantAttr}${hrefAttr}${disabledAttr}>
  Zobacz więcej autorów
</Button>`
  }

  return (
    <div
      className={`flex flex-col items-center space-y-8 p-8 border rounded-lg ${
        cardVariant === 'dark' ? 'border-basic-gray2 bg-dark-blue-dark-blue1' : 'border-basic-gray6 bg-basic-white'
      }`}
    >
      {/* Button */}
      <div>
        <Button
          size={config.size}
          icon={config.icon}
          variant={config.variant || cardVariant}
          disabled={config.disabled}
          href={config.href}
          className={
            config.active
              ? cardVariant === 'dark'
                ? '!bg-dark-blue-dark-blue2 !border-dark-blue-dark-blue6 !text-dark-blue-dark-blue5 hover:!bg-dark-blue-dark-blue2 hover:!border-dark-blue-dark-blue6 hover:!text-dark-blue-dark-blue5'
                : '!bg-dark-blue-dark-blue6 !border-dark-blue-dark-blue1 !text-dark-blue-dark-blue1 hover:!bg-dark-blue-dark-blue6 hover:!border-dark-blue-dark-blue1 hover:!text-dark-blue-dark-blue1'
              : ''
          }
        >
          Zobacz więcej autorów
        </Button>
      </div>

      {/* Info */}
      <div className="text-center space-y-2 w-full">
        <Typography variant="body4" className={cardVariant === 'dark' ? 'text-basic-white' : 'text-basic-black'}>
          {config.label}
        </Typography>

        {/* Copy button */}
        <button
          onClick={() => handleCopy(generateComponentCode())}
          className={`w-full px-2 py-1 text-xs rounded transition-colors ${
            cardVariant === 'dark'
              ? 'bg-basic-gray2 hover:bg-basic-gray3 text-basic-white'
              : 'bg-basic-gray6 hover:bg-basic-gray5 text-basic-black'
          }`}
        >
          {copied ? 'Copied!' : 'Copy Component'}
        </button>

        {/* Component code display */}
        <div className="rounded p-2">
          <code
            className={`text-xs block break-all whitespace-pre ${
              cardVariant === 'dark' ? 'text-basic-white' : 'text-basic-gray3'
            }`}
          >
            {generateComponentCode()}
          </code>
        </div>
      </div>
    </div>
  )
}

export default function ButtonExamples() {
  return (
    <div className="space-y-40">
      <Typography variant="h2">Button Components</Typography>

      <Typography variant="body1">
        Interactive button components with automatic hover, active, and focus states. Buttons are available in three
        sizes (L, M, S) with optional left or right icons.
      </Typography>

      <Typography variant="body1">Try hovering over and clicking the buttons to see the interactive states!</Typography>

      {/* Light Button Examples */}
      <div className="space-y-24">
        <Typography variant="h3" className="h4--caps">
          Light Variations
        </Typography>
        <div className="grid grid-cols-1 t:grid-cols-2 d:grid-cols-3 gap-16">
          {BUTTON_VARIANTS.map((config, index) => (
            <ButtonExample key={`btn-${index}`} config={config} cardVariant="light" />
          ))}
        </div>
      </div>

      {/* Dark Button Examples */}
      <div className="space-y-24">
        <Typography variant="h3" className="h4--caps">
          Dark Variations
        </Typography>
        <div className="grid grid-cols-1 t:grid-cols-2 d:grid-cols-3 gap-16">
          {DARK_BUTTON_VARIANTS.map((config, index) => (
            <ButtonExample key={`dark-btn-${index}`} config={config} cardVariant="dark" />
          ))}
        </div>
      </div>

      {/* Link Button Example */}
      <div className="space-y-24">
        <Typography variant="h3" className="h4--caps">
          Link Usage
        </Typography>
        <Typography variant="body1">
          Provide an `href` prop to render the button as a Next.js `Link`, preserving the same visual treatment while
          navigating like a standard anchor. Use the `link-light` or `link-dark` variants for inline CTA links.
        </Typography>
        <div className="grid grid-cols-1 t:grid-cols-2 d:grid-cols-3 gap-16">
          <ButtonExample config={LINK_BUTTON_VARIANTS[0]} cardVariant="light" />
          <ButtonExample config={LINK_BUTTON_VARIANTS[1]} cardVariant="dark" />
        </div>
      </div>
    </div>
  )
}
